import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { UploadZone } from './workspace/UploadZone';
import { ProcessingView } from './workspace/ProcessingView';
import { ResultsView } from './workspace/ResultsView';
import { ImageModal } from './workspace/ImageModal';

// Confirm Modal Component - styled to match website theme
const ConfirmModal = ({ icon, title, message, confirmText, onConfirm, onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className="text-center space-y-6">
          <div className="text-5xl">{icon}</div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-gray-600">{message}</p>
          </div>

          {/* Confirm button */}
          <button
            onClick={onConfirm}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export const WorkspaceDashboard = () => {
  const location = useLocation();

  // Application state
  const [appState, setAppState] = useState('upload'); // 'upload' | 'preview' | 'processing' | 'results'

  // Upload data
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  // Job data
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobMessage, setJobMessage] = useState('');
  const [results, setResults] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Confirm modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState(null);

  // Handle navigation from history page (in-progress jobs)
  useEffect(() => {
    if (location.state?.jobId) {
      console.log('[Dashboard] Navigated from history with jobId:', location.state.jobId);

      const loadJobFromHistory = async () => {
        try {
          const response = await axios.get(`/api/v1/jobs/${location.state.jobId}`);
          const jobData = response.data;

          console.log('[Dashboard] Loaded job from history:', jobData);

          // Set job data
          setCurrentJobId(jobData.id);
          setJobStatus(jobData.status);
          setJobProgress(jobData.progress || 0);
          setJobMessage(jobData.message || '');

          // Set preview image if available
          if (jobData.image_url) {
            setPreview(jobData.image_url);
          }

          // Set appropriate app state based on job status
          if (jobData.status === 'completed') {
            setResults(jobData.processing_results);
            setAppState('results');
          } else if (jobData.status === 'processing' || jobData.status === 'queued') {
            setAppState('processing');
          } else if (jobData.status === 'canceled') {
            // Canceled job was just resumed via the resume endpoint
            // It should now be in QUEUED status, show processing view
            console.log('[Dashboard] Canceled job loaded - showing processing view');
            setAppState('processing');
          } else if (jobData.status === 'failed') {
            // Job failed - silently return to upload
            console.error('[Dashboard] Job failed:', jobData.error);
            setAppState('upload');
          }
        } catch (error) {
          console.error('[Dashboard] Failed to load job from history:', error);
          // Silently return to upload
          setAppState('upload');
        }
      };

      loadJobFromHistory();

      // Clear the location state to prevent re-loading on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Polling for job status
  useEffect(() => {
    if (appState === 'processing' && currentJobId) {
      console.log(`[Dashboard] Starting polling for job: ${currentJobId}`);

      const pollJobStatus = async () => {
        try {
          const response = await axios.get(`/api/v1/jobs/${currentJobId}`);
          const jobData = response.data;

          setJobStatus(jobData.status);
          setJobProgress(jobData.progress || 0);
          setJobMessage(jobData.message || '');

          console.log(`[Dashboard] Job status: ${jobData.status}, Progress: ${jobData.progress}%`);

          // Check if job is complete
          if (jobData.status === 'completed') {
            console.log('[Dashboard] Job completed, showing results');
            console.log('[Dashboard] Processing results:', jobData.processing_results);
            setResults(jobData.processing_results);
            setAppState('results');
          } else if (jobData.status === 'failed') {
            console.error('[Dashboard] Job failed:', jobData.error);
            toast.error('Processing failed. Please try again.');
            setAppState('upload');
            handleReset();
          }
        } catch (error) {
          console.error('[Dashboard] Failed to poll job status:', error);
        }
      };

      // Initial fetch
      pollJobStatus();

      // Poll every 3 seconds
      const interval = setInterval(pollJobStatus, 3000);

      return () => {
        console.log('[Dashboard] Stopping polling');
        clearInterval(interval);
      };
    }
  }, [appState, currentJobId]);

  // Handler: Upload started
  const handleUploadStarted = (file, previewUrl) => {
    console.log('[Dashboard] Upload started:', file.name);
    setUploadedFile(file);
    setPreview(previewUrl);
    setAppState('preview');
  };

  // Handler: Processing started
  const handleProcessingStarted = async (jobId) => {
    console.log('[Dashboard] Processing started for job:', jobId);
    setCurrentJobId(jobId);
    setAppState('processing');

    // Fetch job details to get preview image and other data
    try {
      const response = await axios.get(`/api/v1/jobs/${jobId}`);
      const jobData = response.data;

      console.log('[Dashboard] Loaded job data:', jobData);

      // Set preview image if available
      if (jobData.image_url) {
        setPreview(jobData.image_url);
      }

      // Update job status and progress
      setJobStatus(jobData.status);
      setJobProgress(jobData.progress || 0);
      setJobMessage(jobData.message || '');

      // If job is already completed, show results
      if (jobData.status === 'completed') {
        setResults(jobData.processing_results);
        setAppState('results');
      } else if (jobData.status === 'canceled') {
        // Job is still in canceled state (shouldn't happen if resumed properly)
        console.warn('[Dashboard] Job still in canceled state during processing');
        setAppState('processing');
      } else if (jobData.status === 'failed') {
        // Job failed - silently return to upload
        console.error('[Dashboard] Job failed:', jobData.error);
        setAppState('upload');
      }
    } catch (error) {
      console.error('[Dashboard] Failed to load job details:', error);
      // Continue anyway - polling will update status
    }
  };

  // Handler: Cancel job - show confirmation modal
  const handleCancel = () => {
    if (!currentJobId) {
      console.warn('[Dashboard] No job ID to cancel');
      handleReset();
      return;
    }

    // Show confirmation modal
    setConfirmModalConfig({
      icon: '⏸️',
      title: 'Stop creating?',
      message: 'You can continue later from History',
      confirmText: 'Stop',
      onConfirm: handleCancelConfirmed
    });
    setConfirmModalOpen(true);
  };

  // Handler: Cancel confirmed - actually cancel the job
  const handleCancelConfirmed = async () => {
    setConfirmModalOpen(false);

    try {
      console.log('[Dashboard] Canceling job:', currentJobId);
      await axios.post(`/api/v1/jobs/${currentJobId}/cancel`);
      console.log('[Dashboard] Job canceled successfully');
      handleReset();
    } catch (error) {
      console.error('[Dashboard] Failed to cancel job:', error);
      // Silently fail and reset anyway
      handleReset();
    }
  };

  // Handler: Change photo
  const handleChangePhoto = () => {
    console.log('[Dashboard] User wants to change photo');
    setAppState('upload');
    setUploadedFile(null);
    setPreview(null);
  };

  // Handler: Create another - show confirmation modal
  const handleCreateAnother = () => {
    console.log('[Dashboard] User wants to create another model');

    // Show confirmation modal
    setConfirmModalConfig({
      icon: '✨',
      title: 'Create another?',
      message: 'Your current design will be saved in History',
      confirmText: 'Create New',
      onConfirm: handleCreateAnotherConfirmed
    });
    setConfirmModalOpen(true);
  };

  // Handler: Create another confirmed - reset to upload
  const handleCreateAnotherConfirmed = () => {
    setConfirmModalOpen(false);
    handleReset();
  };

  // Handler: Reset to initial state
  const handleReset = () => {
    console.log('[Dashboard] Resetting to initial state');
    setAppState('upload');
    setUploadedFile(null);
    setPreview(null);
    setCurrentJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobMessage('');
    setResults(null);
    setModalOpen(false);
    setModalImageIndex(0);
    setConfirmModalOpen(false);
    setConfirmModalConfig(null);
  };

  // Handler: Order button clicked
  const handleOrder = () => {
    console.log('[Dashboard] ORDER IT NOW clicked');
    console.log('[Dashboard] Job ID:', currentJobId);

    // Track analytics
    if (window.gtag) {
      window.gtag('event', 'order_initiated', {
        job_id: currentJobId,
        source: modalOpen ? 'modal' : 'results_page'
      });
    }

    // TODO: Implement order flow
    // Option 1: Navigate to order page
    // navigate(`/order/${currentJobId}`);

    // Option 2: External redirect
    // window.location.href = `/order?job=${currentJobId}`;

    // Option 3: Show order modal
    alert('Order functionality coming soon!');
  };

  // Handler: Open image modal
  const handleOpenModal = (imageIndex) => {
    console.log('[Dashboard] Opening modal for image:', imageIndex);
    setModalImageIndex(imageIndex);
    setModalOpen(true);
  };

  // Handler: Close image modal
  const handleCloseModal = () => {
    console.log('[Dashboard] Closing modal');
    setModalOpen(false);
  };

  // Get display images (original photo, markup, and rendered model)
  const getDisplayImages = () => {
    if (!results) return [];

    const images = [];

    // Original user photo
    if (preview) {
      images.push({
        url: preview,
        label: 'Your Photo',
        subtitle: 'Your original uploaded image'
      });
    }

    // Markup/design preview (prefer whole image, fall back to undershirt)
    const markupUrl = results.markup_whole_url || results.markup_undershirt_url;
    if (markupUrl) {
      images.push({
        url: markupUrl,
        label: 'Design Preview',
        subtitle: 'Processed design template'
      });
    }

    // Rendered 3D model
    if (results.model_image_url) {
      images.push({
        url: results.model_image_url,
        label: '3D Model',
        subtitle: 'Your personalized figure'
      });
    }

    return images;
  };

  const displayImages = getDisplayImages();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Upload State */}
      {(appState === 'upload' || appState === 'preview') && (
        <UploadZone
          appState={appState}
          uploadedFile={uploadedFile}
          preview={preview}
          onUploadStarted={handleUploadStarted}
          onProcessingStarted={handleProcessingStarted}
          onChangePhoto={handleChangePhoto}
        />
      )}

      {/* Processing State */}
      {appState === 'processing' && (
        <ProcessingView
          preview={preview}
          progress={jobProgress}
          message={jobMessage}
          onCancel={handleCancel}
        />
      )}

      {/* Results State */}
      {appState === 'results' && (
        <ResultsView
          preview={preview}
          images={displayImages}
          onOpenModal={handleOpenModal}
          onOrder={handleOrder}
          onCreateAnother={handleCreateAnother}
          onCancel={handleReset}
        />
      )}

      {/* Image Modal */}
      {modalOpen && (
        <ImageModal
          images={displayImages}
          currentIndex={modalImageIndex}
          onClose={handleCloseModal}
          onIndexChange={setModalImageIndex}
          onOrder={handleOrder}
        />
      )}

      {/* Confirm Modal (for cancel and create another) */}
      {confirmModalOpen && confirmModalConfig && (
        <ConfirmModal
          icon={confirmModalConfig.icon}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          confirmText={confirmModalConfig.confirmText}
          onConfirm={confirmModalConfig.onConfirm}
          onClose={() => setConfirmModalOpen(false)}
        />
      )}
    </div>
  );
};
